/**
 * ConnectionErrorBanner
 *
 * Displays a banner at the top of screens when one or more connections
 * require re-authentication or account-selection repair.
 *
 * Tapping "Fix now" opens the provider-specific repair flow for that connection.
 */

import { View, Text, Pressable, Alert, ActivityIndicator } from 'react-native';
import { useState, useEffect, useRef } from 'react';
import { trpc } from '../lib/trpc';
import { usePlaidConnection } from '../hooks/usePlaidConnection';
import { useSnapTradeConnection } from '../hooks/useSnapTradeConnection';
import { useTellerConnection } from '../hooks/useTellerConnection';
import { SnapTradePortal } from './SnapTradePortal';

/** Error codes that indicate the user needs to re-authenticate */
const AUTH_ERROR_CODES = [
  'ITEM_LOGIN_REQUIRED',
  'INVALID_CREDENTIALS',
  'INSUFFICIENT_CREDENTIALS',
  'USER_SETUP_REQUIRED',
  'MFA_NOT_SUPPORTED',
  'INSTITUTION_DOWN',
];
const PLAID_ACCOUNT_SELECTION_ERROR_CODE = 'PLAID_ACCOUNT_SELECTION_REQUIRED';

interface ConnectionErrorBannerProps {
  /** Called after successful re-auth so parent can refresh data */
  onReconnected?: () => void;
}

export function ConnectionErrorBanner({ onReconnected }: ConnectionErrorBannerProps) {
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);

  const connectionsQ = trpc.connections.list.useQuery();
  const syncMutation = trpc.connections.sync.useMutation();
  const errorConnections = (connectionsQ.data ?? []).filter(
    (c) =>
      (
        c.status === 'error'
        || c.status === 'disconnected'
        || c.errorCode === PLAID_ACCOUNT_SELECTION_ERROR_CODE
      ) && !(c.isLocalClone && c.status === 'disconnected' && !c.errorCode),
  );

  const isAuthError = (errorCode: string | null) =>
    errorCode != null && AUTH_ERROR_CODES.includes(errorCode);
  const isPlaidAccountSelectionError = (provider: string, errorCode: string | null) =>
    provider === 'plaid' && errorCode === PLAID_ACCOUNT_SELECTION_ERROR_CODE;
  const isTellerReconnectError = (errorCode: string | null) =>
    errorCode != null && errorCode.startsWith('disconnected');

  // Track whether we need to auto-open Plaid Link after reconnectingId settles
  const pendingPlaidReconnect = useRef(false);
  const pendingTellerReconnect = useRef(false);

  const { open: openPlaid, isLoading: plaidLoading } = usePlaidConnection({
    connectionId: reconnectingId ?? undefined,
    onSuccess: () => {
      setReconnectingId(null);
      connectionsQ.refetch();
      onReconnected?.();
    },
    onError: (err) => {
      setReconnectingId(null);
      Alert.alert('Reconnection Error', err);
    },
  });

  // Open Plaid Link after re-render so the hook has the correct connectionId
  useEffect(() => {
    if (pendingPlaidReconnect.current && reconnectingId) {
      pendingPlaidReconnect.current = false;
      openPlaid();
    }
  }, [reconnectingId, openPlaid]);

  const snaptrade = useSnapTradeConnection({
    onSuccess: () => {
      setReconnectingId(null);
      connectionsQ.refetch();
      onReconnected?.();
    },
    onError: (err) => {
      setReconnectingId(null);
      Alert.alert('Reconnection Error', err);
    },
  });

  const teller = useTellerConnection({
    connectionId: reconnectingId ?? undefined,
    onSuccess: () => {
      setReconnectingId(null);
      connectionsQ.refetch();
      onReconnected?.();
    },
    onError: (err) => {
      setReconnectingId(null);
      Alert.alert('Reconnection Error', err);
    },
  });

  useEffect(() => {
    if (pendingTellerReconnect.current && reconnectingId) {
      pendingTellerReconnect.current = false;
      teller.open();
    }
  }, [reconnectingId, teller.open]);

  if (errorConnections.length === 0) return null;

  const isReconnecting = (id: string, provider: string) =>
    reconnectingId === id &&
    (
      provider === 'plaid'
        ? plaidLoading
        : provider === 'teller'
          ? teller.isLoading
          : snaptrade.isLoading || syncMutation.isPending
    );

  const handleReconnect = async (conn: { id: string; provider: string; errorCode: string | null }) => {
    const needsAuth = isPlaidAccountSelectionError(conn.provider, conn.errorCode)
      || (conn.provider === 'teller'
        ? isTellerReconnectError(conn.errorCode)
        : isAuthError(conn.errorCode));

    if (!needsAuth) {
      // Sync error — retry the sync instead of re-authenticating
      setReconnectingId(conn.id);
      try {
        await syncMutation.mutateAsync({ id: conn.id });
        connectionsQ.refetch();
        onReconnected?.();
      } catch (err) {
        Alert.alert('Sync Error', err instanceof Error ? err.message : 'Sync failed');
      } finally {
        setReconnectingId(null);
      }
      return;
    }

    if (conn.provider === 'plaid') {
      pendingPlaidReconnect.current = true;
      setReconnectingId(conn.id);
    } else if (conn.provider === 'teller') {
      pendingTellerReconnect.current = true;
      setReconnectingId(conn.id);
    } else {
      setReconnectingId(conn.id);
      snaptrade.open();
    }
  };

  return (
    <>
      <SnapTradePortal
        loginLink={snaptrade.oauthUrl}
        isOpen={snaptrade.isOpen}
        onClose={() => {
          snaptrade.close();
          setReconnectingId(null);
        }}
        onSuccess={snaptrade.handleSuccess}
        onError={(err) => {
          snaptrade.handleError(err);
          setReconnectingId(null);
        }}
      />
      <View className="mx-4 mt-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 gap-2">
        <View className="flex-row items-center gap-2">
          <Text className="text-amber-600 dark:text-amber-400 font-bold text-sm">Action Required</Text>
        </View>
        {errorConnections.map((conn) => {
          const needsPlaidAccountSelection = isPlaidAccountSelectionError(
            conn.provider,
            conn.errorCode,
          );
          const needsAuth = needsPlaidAccountSelection
            || (conn.provider === 'teller'
              ? isTellerReconnectError(conn.errorCode)
              : isAuthError(conn.errorCode));
          const busy = isReconnecting(conn.id, conn.provider);
          return (
            <View
              key={conn.id}
              className="flex-row items-center justify-between"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  {conn.institutionName ?? 'Unknown Institution'}
                </Text>
                <Text className="text-xs text-amber-600 dark:text-amber-400">
                  {needsPlaidAccountSelection
                    ? 'Reconnect to choose which accounts Per Diem can access'
                    : needsAuth
                    ? conn.provider === 'teller'
                      ? 'Reconnect required — Teller marked the enrollment disconnected'
                      : 'Login required — credentials changed'
                    : 'Sync failed — tap to retry'}
                </Text>
              </View>
              <Pressable
                onPress={() => handleReconnect(conn)}
                disabled={busy}
                className="bg-amber-500 px-3 py-1.5 rounded-lg"
              >
                {busy ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text className="text-white text-xs font-semibold">
                    {needsAuth ? 'Fix now' : 'Retry'}
                  </Text>
                )}
              </Pressable>
            </View>
          );
        })}
      </View>
    </>
  );
}
